// Ported from packages/desk/src/mobile/AppIcon.tsx (docs/PLAN-2026-09-21-mobile-os-shell.md, step 4). Same file name and props
// as the desk copy, the dk- prefix renamed rdr-, so a fix in one is a copy in the other. Radar never imports @gamo/desk.
/**
 * One app on the phone: a 72 px translucent tile with the app's glyph, its name under it, and a badge. Quiet by default;
 * the accent (the host's orange) only for an app with something new or the one just opened, so orange means "look
 * here" and not "this is an icon". A tap opens it; a long press, a right click or the context-menu key opens its quick
 * actions.
 */
import { useRef, type ReactNode } from "react";
import { useLongPress } from "./gestures";
import { badgeLabel, formatBadge } from "./layout";
import type { BadgeValue } from "./types";

export function AppIcon({
  id,
  name,
  icon,
  badge,
  accent = false,
  hasActions = true,
  onOpen,
  onActions,
  data = "app",
}: {
  id: string;
  name: string;
  icon: ReactNode;
  badge?: BadgeValue;
  accent?: boolean;
  /** Whether a long press does anything; More has no quick actions. */
  hasActions?: boolean;
  onOpen: () => void;
  onActions?: (anchor: HTMLElement) => void;
  /** The `data-rdr-*` hook, `app` (with the id) for an app, `more` for More. */
  data?: "app" | "more";
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const press = useLongPress(() => {
    if (ref.current) onActions?.(ref.current);
  }, !hasActions || !onActions);
  const b = formatBadge(badge);
  const extra = badgeLabel(badge);
  const hook = data === "more" ? { "data-rdr-more": "" } : { "data-rdr-app": id };
  return (
    <button
      ref={ref}
      type="button"
      className={"rdr-micon" + (accent ? " accent" : "")}
      aria-label={extra ? `${name}, ${extra}` : name}
      aria-haspopup={hasActions && onActions ? "menu" : undefined}
      onClick={onOpen}
      {...hook}
      {...press}
    >
      <span className="rdr-micon-tile" aria-hidden="true">
        {icon}
        {b.kind === "count" && <span className="rdr-mbadge">{b.text}</span>}
        {b.kind === "dot" && <span className="rdr-mbadge dot" />}
      </span>
      <span className="rdr-micon-name" aria-hidden="true">
        {name}
      </span>
    </button>
  );
}
